/**
 * fsSession.js — sessione forward-secret 1:1: compone prekey + X3DH + ratchet.
 * ⚠️ MODULO DA AUDIRE. Cutover pulito (ratchet-only, niente fallback statico).
 *
 *   publishMyPrekeys(): genera SPK firmata + OPK, le pubblica, ritorna i PRIVATI
 *     (in app vanno in IndexedDB; nel test restano in memoria) per fare il responder.
 *   openOutgoing(peer): fetch bundle + verifica firma SPK + X3DH(iniziatore) +
 *     ratchet.initAlice → { session, prekeyMsg }. prekeyMsg viaggia col 1° messaggio.
 *   openIncoming(prekeyMsg, mySpk, myOpk): X3DH(destinatario) + ratchet.initBob → session.
 *   encryptMsg/decryptMsg: ratchet.
 *
 * Identità = chiavi statiche già pubblicate dall'app:
 *   box  = boxKeypairFromSeed(seed)  (X25519, = pubKeyBox)
 *   sign = keypairFromSeed(seed)     (Ed25519, = pubKeySign, firma la SPK)
 */

import { mnemonicToSeed } from './mnemonic';
import {
  initSodium, boxKeypairFromSeed, keypairFromSeed, sign, verify, toBase64Url, fromBase64Url,
} from './crypto';
import { genKeypair, initAlice, initBob, encrypt, decrypt } from './ratchet';
import { x3dhInitiator, x3dhResponder, mixPq } from './x3dh';
import { pqKeygen, pqEncapsulate, pqDecapsulate } from './pqkem';
import { publishPrekeys, fetchPrekeyBundle } from './prekeys';

const asBytes = async (k) => (typeof k === 'string' ? fromBase64Url(k) : k);

/**
 * Risolve l'identità da chiavi GIÀ derivate (ChatScreen passa la keypair box)
 * oppure, in fallback, dalla mnemonica (test/onboarding). box = X25519
 * (pubKeyBox), sign = Ed25519 (firma la SPK).
 */
async function resolveIdentity({ mnemonic, boxKeypair, signKeypair }) {
  if (boxKeypair || signKeypair) {
    return {
      box: boxKeypair ? { pub: await asBytes(boxKeypair.pub), priv: await asBytes(boxKeypair.priv) } : null,
      sign: signKeypair ? { pub: await asBytes(signKeypair.pub), priv: await asBytes(signKeypair.priv) } : null,
    };
  }
  const seed = mnemonicToSeed(mnemonic);
  const box = await boxKeypairFromSeed(seed);   // {publicKey, secretKey}
  const sgn = await keypairFromSeed(seed);       // {publicKey, secretKey}
  return {
    box: { pub: box.publicKey, priv: box.secretKey },
    sign: { pub: sgn.publicKey, priv: sgn.secretKey },
  };
}
function randId() {
  const b = crypto.getRandomValues(new Uint8Array(12));
  let s = ''; b.forEach((x) => { s += String.fromCharCode(x); });
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Genera e pubblica le prekey del chiamante. Ritorna i PRIVATI da custodire. */
export async function publishMyPrekeys({ mnemonic, signKeypair, sigil, count = 20 }) {
  await initSodium();
  const id = await resolveIdentity({ mnemonic, signKeypair });
  const spkKp = genKeypair();
  const spkId = randId();
  const spkSig = await sign(spkKp.pub, id.sign.priv); // firma la SPK con l'identità
  const pq = pqKeygen(); // chiave KEM post-quantum (medio termine, come la SPK)
  const opks = [];
  for (let i = 0; i < count; i++) opks.push({ id: randId(), kp: genKeypair() });

  await publishPrekeys({
    sigil,
    spkPub: await toBase64Url(spkKp.pub),
    spkSig: await toBase64Url(spkSig),
    spkId,
    pqPub: await toBase64Url(pq.pub),
    opks: await Promise.all(opks.map(async (o) => ({ id: o.id, pub: await toBase64Url(o.kp.pub) }))),
  });

  return {
    spk: { id: spkId, pub: spkKp.pub, priv: spkKp.priv },
    pq: { pub: pq.pub, secret: pq.secret }, // segreto KEM da custodire (per decapsulare)
    opks: opks.map((o) => ({ id: o.id, pub: o.kp.pub, priv: o.kp.priv })),
  };
}

/** INIZIATORE: apre una sessione verso `peerSigil`. `bundle` opzionale: se passato
 *  (es. dal canale sealed che lo prende in modo ANONIMO) si evita il fetch
 *  autenticato che rivelerebbe il mittente. */
export async function openOutgoing({ mnemonic, boxKeypair, peerSigil, bundle }) {
  await initSodium();
  const id = await resolveIdentity({ mnemonic, boxKeypair });
  const b = bundle || await fetchPrekeyBundle(peerSigil);
  if (!b.identityKeyBox || !b.identityKeySign || !b.spk) throw new Error('PEER_PREKEYS_MISSING');

  const peerIKbox = await fromBase64Url(b.identityKeyBox);
  const peerIKsign = await fromBase64Url(b.identityKeySign);
  const spkPub = await fromBase64Url(b.spk.pub);
  const spkSig = await fromBase64Url(b.spk.sig);
  if (!(await verify(spkSig, spkPub, peerIKsign))) throw new Error('SPK_SIGNATURE_INVALID');

  const opkPub = b.opk ? await fromBase64Url(b.opk.pub) : null;
  const EK = genKeypair();
  let SK = await x3dhInitiator(id.box, EK, peerIKbox, spkPub, opkPub);
  const prekeyMsg = {
    ikA: await toBase64Url(id.box.pub),
    ekA: await toBase64Url(EK.pub),
    spkId: b.spk.id,
    opkId: b.opk ? b.opk.id : null,
  };
  // Ibrido post-quantum: se il destinatario ha una chiave KEM, incapsula e
  // mescola il segreto PQ in SK. pqCt viaggia col prekey-message.
  if (b.pqPub) {
    const { ct, ss } = pqEncapsulate(await fromBase64Url(b.pqPub));
    SK = await mixPq(SK, ss);
    prekeyMsg.pqCt = await toBase64Url(ct);
  }
  const session = await initAlice(SK, spkPub);
  // peerBoxPub: la box-key pubblica del destinatario (per sigillare il prekeyMsg
  // verso di lui nel sealed-sender, così ikA non finisce in chiaro sul server).
  return { session, prekeyMsg, peerBoxPub: b.identityKeyBox };
}

/** DESTINATARIO: apre la sessione dal `prekeyMsg`, usando le proprie SPK/OPK/PQ. */
export async function openIncoming({ mnemonic, boxKeypair, prekeyMsg, mySpk, myOpk, myPq }) {
  await initSodium();
  const id = await resolveIdentity({ mnemonic, boxKeypair });
  const ikA = await fromBase64Url(prekeyMsg.ikA);
  const ekA = await fromBase64Url(prekeyMsg.ekA);
  const opk = myOpk ? { pub: myOpk.pub, priv: myOpk.priv } : null;
  let SK = await x3dhResponder(id.box, { pub: mySpk.pub, priv: mySpk.priv }, opk, ikA, ekA);
  // Ibrido post-quantum: se il prekey-message porta un ciphertext KEM e ho il
  // segreto PQ corrispondente, decapsula e mescola in SK.
  if (prekeyMsg.pqCt && myPq) {
    const ss = pqDecapsulate(await fromBase64Url(prekeyMsg.pqCt), myPq.secret);
    SK = await mixPq(SK, ss);
  }
  const session = await initBob(SK, { pub: mySpk.pub, priv: mySpk.priv });
  return { session };
}

export const encryptMsg = (session, text) => encrypt(session, text);
export const decryptMsg = (session, header, ct) => decrypt(session, header, ct);
